import { watch, onUnmounted } from 'vue';

/**
 * Lock the host page's scroll while a flag is true — used to freeze the
 * background when the mobile drawer is open.
 *
 *   useScrollLock(open);                       // lock on mobile only (default)
 *   useScrollLock(open, '(max-width: 480px)'); // custom breakpoint
 *
 * On desktop the side panel only covers ~420px and the page beside it should
 * stay scrollable, so the lock is gated behind a media query and is a no-op
 * when it doesn't match.
 *
 * Uses the `position: fixed` technique (rather than `overflow: hidden`, which
 * iOS Safari ignores): we pin <body> at its current offset, which both blocks
 * background scrolling AND prevents the horizontal rubber-band pan. The scroll
 * position is captured on lock and restored on unlock so the page never jumps.
 */
const DEFAULT_QUERY = '(max-width: 640px)';

/**
 * Pure, injectable core so the lock/unlock behaviour can be unit-tested without
 * a DOM or a Vue component instance.
 *
 * @param {{ win: Window, doc: Document, mediaQuery?: string }} env
 * @returns {{ lock: () => void, unlock: () => void, isLocked: () => boolean }}
 */
export function createScrollLock({ win, doc, mediaQuery = DEFAULT_QUERY }) {
    let scrollY = 0;
    let locked = false;

    function lock() {
        if (locked || !win || !doc) {
            return;
        }
        if (mediaQuery && !win.matchMedia(mediaQuery).matches) {
            return;
        }
        scrollY = win.scrollY;
        const { style } = doc.body;
        style.position = 'fixed';
        style.top = `-${scrollY}px`;
        style.left = '0';
        style.right = '0';
        style.width = '100%';
        style.overflow = 'hidden';
        // Pinning <body> stops scrolling but NOT Chrome's elastic overscroll:
        // a swipe still rubber-bands the viewport and reveals the host page
        // behind the fixed drawer. `overscroll-behavior: none` on the root
        // scroller (<html>) kills the bounce and pull-to-refresh; we also set
        // it on <body> as a fallback for engines that scroll the body.
        style.overscrollBehavior = 'none';
        if (doc.documentElement) {
            doc.documentElement.style.overscrollBehavior = 'none';
        }
        locked = true;
    }

    function unlock() {
        if (!locked) {
            return;
        }
        const { style } = doc.body;
        style.position = '';
        style.top = '';
        style.left = '';
        style.right = '';
        style.width = '';
        style.overflow = '';
        style.overscrollBehavior = '';
        if (doc.documentElement) {
            doc.documentElement.style.overscrollBehavior = '';
        }
        win.scrollTo(0, scrollY);
        locked = false;
    }

    return { lock, unlock, isLocked: () => locked };
}

export function useScrollLock(isLocked, mediaQuery = DEFAULT_QUERY) {
    if (typeof window === 'undefined') {
        return;
    }

    const { lock, unlock } = createScrollLock({ win: window, doc: document, mediaQuery });

    watch(isLocked, (value) => {
        value ? lock() : unlock();
    });

    // Safety net: never leave <body> pinned if the widget unmounts while open.
    onUnmounted(unlock);
}
